import React from "react";
import OverallPerformanceCard from "./OverallPerformanceCard";
import PeerComparison from "./PeerComparison";
import CoreCompetencies from "./CoreCompetencies";
import CaseAnalysis from "./CaseAnalysis";
import DevelopmentPlan from "./DevelopmentPlan";

// Importing the sub-interfaces from your new interface file
import {
  PerformanceSummary,
  PeerComparison as PeerComparisonType,
  CompetencyScores,
  ComponentAnalysis,
  DevelopmentPlan as DevelopmentPlanType,
} from "../../types";

interface OverallPerformanceProps {
  performanceSummary?: PerformanceSummary | null;
  peerComparison?: PeerComparisonType | null;
  competencyScores?: CompetencyScores;
  componentAnalysis?: ComponentAnalysis;
  developmentPlan?: DevelopmentPlanType;
}

const OverallPerformance: React.FC<OverallPerformanceProps> = ({
  performanceSummary,
  peerComparison,
  competencyScores,
  componentAnalysis,
  developmentPlan,
}) => {
  // 1. Safely extract values from performanceSummary to pass to OverallPerformanceCard
  const score = performanceSummary?.overallPerformance.score ?? 0;
  const maxScore = performanceSummary?.overallPerformance.maxScore ?? 0;
  const ratingLevel =
    performanceSummary?.overallPerformance.ratingLevel ?? "Meets";
  const strengths = performanceSummary?.overallPerformance.strengths ?? "";
  const improvements =
    performanceSummary?.overallPerformance.improvements ?? "";

  return (
    <div className="w-full h-screen overflow-hidden">
      <div
        className="grid w-full h-full gap-2"
        style={{
          gridTemplateColumns: "repeat(6, 1fr)",
          gridTemplateRows: "repeat(4, 1fr)",
          gridAutoFlow: "dense",
        }}
      >
        {/* Performance Card - 1x3 height */}
        <div className="row-span-3 col-span-2">
          <OverallPerformanceCard
            score={score}
            maxScore={maxScore}
            ratingLevel={ratingLevel}
            strengths={strengths}
            improvements={improvements}
          />
        </div>

        {/* Left column stack */}
        <div className="row-span-3 col-span-1 grid grid-rows-2 gap-2">
          <div className="row-span-1 col-span-1">
            <PeerComparison
              yourPercentile={peerComparison?.yourPercentile ?? null}
            />
          </div>
          <div className="row-span-1">
            {/* 
              NOTE: If your <CoreCompetencies> component still expects an array
              (like [{ name: string; score: number; ... }]),
              you need to transform "competencyScores" into an array OR
              refactor <CoreCompetencies> to accept the new shape.
            */}
            <CoreCompetencies competencies={competencyScores} />
          </div>
        </div>

        {/* Case Analysis - 2x3 height */}
        <div className="row-span-3 col-span-3">
          {/* 
            If <CaseAnalysis> used to expect an array of sections, 
            you may need to adapt it to handle the four sub-sections 
            (opening_and_structure, brainstorming, etc.) 
            or transform them into an array.
          */}
          <CaseAnalysis componentAnalysis={componentAnalysis} />
        </div>

        {/* Development Plan - 4x1 height */}
        <div className="row-span-1 col-span-4">
          {/* 
            If <DevelopmentPlan> expects a "tasks" array, 
            pass `developmentPlan.area` OR update <DevelopmentPlan> 
            to consume the new shape.
          */}
          {/* <DevelopmentPlan developmentPlan={developmentPlan} /> */}
        </div>
      </div>
    </div>
  );
};

export default OverallPerformance;
